export const CHECKOUT_URL = "https://canbus-en.impultienda.ar/checkout/";

export const images = {
  mockup: "https://cdn.magicpatterns.com/uploads/nManmjGc3mVmbVmQsDjXJr/CANBUS-EN.png",
  author: "https://cdn.magicpatterns.com/uploads/hiJFfSH2tqnGQfMcoXfYLD/JASON_MITCHELL_-_ECU_DECISION.png",
};

export const beforeItems = [
  "You have fault codes but aren't always sure how to organize the investigation.",
  "You jump between wiring diagrams, technical notes, and online resources.",
  "You spend time repeating checks because your findings aren't documented consistently.",
  "You struggle to distinguish confirmed evidence from possible causes.",
  "You find it difficult to keep track of what you've already checked.",
  "Every complicated case seems to require starting from scratch.",
];

export const afterItems = [
  "You follow a structured process for organizing a new case.",
  "You classify symptoms and record the information that matters.",
  "You use decision maps to plan your next investigative step.",
  "You document measurements, observations, and test results in one place.",
  "You track hypotheses instead of treating every possible cause as a confirmed fault.",
  "You finish with a clearer record of what was checked and what still needs verification.",
];

export const situations = [
  {
    title: "You have multiple communication fault codes, but no clear starting point.",
    body: "You know the vehicle has a problem. What you need is a structured way to classify the symptoms and organize the investigation.",
  },
  {
    title: "You spend too much time switching between different technical resources.",
    body: "Your information is scattered across diagrams, notes, saved files, and previous cases. Finding what you need becomes part of the problem.",
  },
  {
    title: "You aren't always sure which diagnostic step makes the most sense next.",
    body: "You don't want to perform random tests. You want to select the next check based on the evidence available and the applicable vehicle-specific procedures.",
  },
  {
    title: "You repeat checks because your previous findings weren't recorded properly.",
    body: "Without consistent documentation, it becomes difficult to remember exactly what was tested, what the results were, and what those results established.",
  },
  {
    title: "Intermittent communication problems make your investigation even harder.",
    body: "The fault may not appear under the same conditions every time. You need a way to record the circumstances, observations, and results associated with each occurrence.",
  },
  {
    title: "You know your diagnostic skills could be more efficient, but your process isn't consistent.",
    body: "You have the tools. You have experience. What you want is a repeatable framework for putting everything together.",
  },
];

export const volumes = [
  {
    number: "I",
    color: "#1463F3",
    title: "Build Your CAN Bus Investigation from the First Symptom",
    intro: "Before you start testing, you need to understand what you're investigating. This volume shows you how to organize the initial information so you approach the case with a clearer starting point.",
    items: [
      "The CAN Bus Case Intake Framework",
      "A symptom and communication-fault classification worksheet",
      "A diagnostic information collection checklist",
      "A case prioritization matrix",
      "A vehicle and system identification record",
      "A framework for distinguishing reported symptoms from verified observations",
      "A preliminary investigation map",
    ],
    result: "A more organized starting point for your next CAN Bus case.",
  },
  {
    number: "II",
    color: "#16A34A",
    title: "Map Your Investigation Before You Start Changing Anything",
    intro: "Once you've collected the initial information, the next challenge is organizing the investigation. This volume turns a complicated case into a visual sequence of questions and decisions.",
    items: [
      "The CAN Bus Investigation Blueprint",
      "A visual case-mapping framework",
      "Decision-tree templates",
      "A symptom-to-investigation planning matrix",
      "A hypothesis tracking worksheet",
      "A diagnostic next-step planner",
      "A guide to recording why a particular check was selected",
    ],
    result: "A structured way to plan your investigation and keep track of your reasoning.",
  },
  {
    number: "III",
    color: "#EA7A0C",
    title: "Organize Your Evidence Instead of Chasing Possible Causes",
    intro: "A fault code, a measurement, an observation, and a confirmed root cause are not the same thing. This volume helps you separate them.",
    items: [
      "The CAN Bus Evidence Tracker",
      "A measurement and observation recording system",
      "A hypothesis-versus-evidence matrix",
      "A test result documentation sheet",
      "Expected vs. observed results framework (when reliable specs are available)",
      "A checklist for identifying missing information",
      "A decision record for each investigative step",
    ],
    result: "A clearer record of what your investigation has established—and what remains uncertain.",
  },
  {
    number: "IV",
    color: "#DC2626",
    title: "Investigate Communication Problems with a More Organized Process",
    intro: "Different symptoms can require different investigative approaches. This volume gives you a structured way to organize common categories of CAN Bus cases without assuming every vehicle follows the same procedure.",
    items: [
      "A communication-fault investigation worksheet",
      "A multiple-module symptom classification framework",
      "An intermittent-fault recording template",
      "A no-communication case organizer",
      "A post-repair investigation checklist",
      "A network investigation planning map",
      "A framework for selecting the next check using evidence and service information",
    ],
    result: "A more consistent method for organizing different types of communication investigations.",
    note: "The templates are organizational tools, not universal vehicle-specific repair procedures. Technical checks and specifications must be confirmed against applicable manufacturer information.",
  },
  {
    number: "V",
    color: "#7C3AED",
    title: "Turn Diagnostic Findings into Clear Next-Step Decisions",
    intro: "An investigation becomes more useful when each result helps you determine what to do next. This volume organizes the relationship between test results, working hypotheses, and subsequent steps.",
    items: [
      "The Diagnostic Decision Matrix",
      "A confirmed-versus-unconfirmed finding checklist",
      "A next-step selection worksheet",
      "A diagnostic dead-end review framework",
      "A repeated-test tracking sheet",
      "A verification planning checklist",
      "A decision closure record",
    ],
    result: "A repeatable framework for documenting why you continued, changed direction, or closed a line of investigation.",
  },
  {
    number: "VI",
    color: "#0891B2",
    title: "Document the Case and Verify the Outcome",
    intro: "The job isn't finished simply because a fault code disappears or a symptom temporarily stops. Your findings need to be documented, and the relevant verification procedures completed.",
    items: [
      "The CAN Bus Diagnostic Case Report",
      "A pre-repair findings summary",
      "A repair and intervention record",
      "A post-repair verification checklist",
      "A remaining-fault documentation sheet",
      "A case closure worksheet",
      "A reusable template for future investigations",
      "A framework for building a personal reference library from completed cases",
    ],
    result: "A clearer, more professional record of the investigation, the evidence collected, and the verification performed.",
  },
];

export const toolkit = [
  {
    title: "Visual Diagnostic Workflows",
    body: "Map the investigation from the initial symptom through evidence collection, decision-making, and verification.",
  },
  {
    title: "Decision Trees and Investigation Maps",
    body: "Organize the questions and potential next steps that arise during a case.",
  },
  {
    title: "Evidence and Measurement Trackers",
    body: "Record observations, measurements, conditions, and test results consistently.",
  },
  {
    title: "Diagnostic Case Templates",
    body: "Keep the information from each investigation organized in one place.",
  },
  {
    title: "Professional Checklists",
    body: "Use repeatable prompts to help avoid overlooking important documentation and verification steps.",
  },
];

export const bonuses = [
  {
    number: 1,
    title: "CAN Bus Quick Reference Cards™",
    value: 37,
    image: "https://cdn.magicpatterns.com/uploads/gtZRP2jyNFwsE7K26ZwfsQ/BONUS_1_CANBUS-EN.png",
    intro: "A collection of concise reference cards designed to help you keep the investigation process organized.",
    items: [
      "Case intake reminders",
      "Evidence recording prompts",
      "Diagnostic documentation checkpoints",
      "Hypothesis tracking reminders",
      "Case closure and verification prompts",
    ],
    outro: "Use the cards as a quick reminder of the process when starting or reviewing a case.",
  },
  {
    number: 2,
    title: "The 10-Minute CAN Bus Case Organization Protocol™",
    value: 27,
    image: "https://cdn.magicpatterns.com/uploads/aMfZoPiQ1vKjziVwuoN8vC/BONUS_2_CANBUS-EN.png",
    intro: "A focused workflow designed to help you organize the initial investigation in approximately 10 minutes.",
    items: [
      "Recording the reported symptom",
      "Identifying the available diagnostic information",
      "Listing the known facts",
      "Separating observations from assumptions",
      "Identifying missing information",
      "Selecting a logical next investigative step",
    ],
    outro: "The objective is to organize the beginning of the investigation—not to promise a complete diagnosis in 10 minutes.",
  },
  {
    number: 3,
    title: "CAN Bus Troubleshooting Review Library™",
    value: 37,
    image: "https://cdn.magicpatterns.com/uploads/5uoCcUupYXs9uPsCTsNW4w/BONUS_3_CANBUS-EN.png",
    intro: "Structured review worksheets for examining common categories of communication-related diagnostic cases.",
    items: [
      "Multiple communication-related fault codes",
      "Modules that do not communicate",
      "Intermittent symptoms",
      "Problems reported after a repair",
      "Cases with incomplete or conflicting evidence",
    ],
    outro: "Each template helps you document the symptom, available evidence, unanswered questions, and next steps.",
  },
  {
    number: 4,
    title: "Professional Diagnostic Case Report Kit™",
    value: 27,
    image: "https://cdn.magicpatterns.com/uploads/jLUrmc8TPdX8kSiwE2VW7H/BONUS_4_CANBUS-EN.png",
    intro: "A documentation kit to help you present your findings in a clear and consistent format.",
    items: [
      "Initial case reports",
      "Diagnostic findings summaries",
      "Measurement records",
      "Repair documentation forms",
      "Verification reports",
      "Case closure summaries",
    ],
    outro: "Improve the consistency of your records and make completed cases easier to review.",
  },
  {
    number: 5,
    title: "30 CAN Bus Investigation Checklists™",
    value: 29,
    image: "https://cdn.magicpatterns.com/uploads/foLpFAvPEK2neNCJJ5gbSB/BONUS_5_CANBUS-EN.png",
    intro: "Checklist templates organized around different stages of a diagnostic investigation.",
    items: [
      "Initial information collection & symptom classification",
      "Evidence recording & test planning",
      "Hypothesis review & measurement documentation",
      "Repair records & post-repair verification",
      "Final case documentation",
    ],
    outro: "Make your process more consistent without forcing every vehicle or fault into the same procedure.",
  },
];

export const testimonials = [
  {
    name: "Mike R.",
    role: "Automotive Technician · Independent Shop",
    photo: "https://cdn.magicpatterns.com/patterns/generated-images/eb3fa72d-697a-4219-8ea0-01697d92e14e.jpg",
    quote:
      "What I like most is the intake worksheet. Before, my notes on network cases were all over the place. Now every case starts on the same page and I can see what I've actually confirmed.",
  },
  {
    name: "Laura G.",
    role: "Automotive Electronics Technician",
    photo: "https://cdn.magicpatterns.com/patterns/generated-images/4f7be1c1-134b-497f-805e-7131053d6ad3.jpg",
    quote:
      "The hypothesis-versus-evidence matrix changed how I document intermittent faults. I stopped mixing what I suspect with what I've measured, and my case reports are much cleaner.",
  },
  {
    name: "Dave P.",
    role: "Shop Owner · 25+ Years in the Trade",
    photo: "https://cdn.magicpatterns.com/patterns/generated-images/b7ad29e5-d426-4d0c-9519-809ec44fbbb2.jpg",
    quote:
      "I printed the checklists for my team. It doesn't replace service information, and it doesn't pretend to—it just gives everyone the same structure for recording what was checked and why.",
  },
];

export const faqs = [
  {
    q: "How will I receive the product?",
    a: "After your payment is confirmed, you'll receive instructions to access and download the digital materials. The product is delivered digitally. No physical item will be shipped.",
  },
  {
    q: "Is this suitable for beginners?",
    a: "The blueprint is designed for people who want a more structured way to organize CAN Bus investigations. Some familiarity with automotive diagnostics and electronic systems will help you apply the worksheets. It is not a substitute for foundational training in automotive electrical systems or CAN Bus communication.",
  },
  {
    q: "Does this work with every vehicle?",
    a: "The organizational framework can help you structure different types of cases, but diagnostic procedures vary by vehicle, network architecture, and fault condition. Always consult the applicable manufacturer's service information. The blueprint does not guarantee a diagnosis for every vehicle or fault.",
  },
  {
    q: "Will this tell me exactly which component is faulty?",
    a: "No. The blueprint is a diagnostic organization and decision-support tool. It helps you record evidence, structure an investigation, and identify logical next steps. The actual diagnosis depends on the vehicle, available information, test results, and your technical judgment.",
  },
  {
    q: "Do I need specialized software?",
    a: "The product consists of digital documents and templates. You don't need to install a dedicated application or subscribe to a proprietary platform. You will still need the appropriate diagnostic equipment, technical references, and tools for your work.",
  },
  {
    q: "Are the files downloadable?",
    a: "Yes. The materials are provided digitally for you to download and consult. You can keep the files for reference and print suitable worksheets if you prefer working with paper.",
  },
  {
    q: "Is this a subscription?",
    a: "No. The advertised offer is a one-time payment of $27 USD, with no recurring subscription fee.",
  },
  {
    q: "Will I receive future updates?",
    a: "The purchase includes the materials listed on this page. Future updates or additional products are included only when explicitly stated in the offer.",
  },
  {
    q: "What if the product isn't right for me?",
    a: "Your purchase is covered by a seven-day money-back guarantee. Contact us within seven days of purchase to request a refund under the guarantee terms.",
  },
  {
    q: "Does this replace manufacturer service information?",
    a: "No. The blueprint helps you organize your diagnostic process. Vehicle-specific wiring diagrams, specifications, service procedures, and safety requirements must be obtained from appropriate sources and verified before performing diagnostic work.",
  },
];

export const recentBuyers = [
  { name: "Carlos", country: "Mexico", flag: "🇲🇽" },
  { name: "Andrés", country: "Colombia", flag: "🇨🇴" },
  { name: "Javier", country: "Spain", flag: "🇪🇸" },
  { name: "Martín", country: "Argentina", flag: "🇦🇷" },
  { name: "Luis", country: "Peru", flag: "🇵🇪" },
  { name: "Rodrigo", country: "Chile", flag: "🇨🇱" },
  { name: "José", country: "Ecuador", flag: "🇪🇨" },
  { name: "Miguel", country: "Dominican Republic", flag: "🇩🇴" },
  { name: "Diego", country: "Uruguay", flag: "🇺🇾" },
  { name: "Fernando", country: "Guatemala", flag: "🇬🇹" },
  { name: "Ricardo", country: "Costa Rica", flag: "🇨🇷" },
  { name: "Pablo", country: "Bolivia", flag: "🇧🇴" },
  { name: "Héctor", country: "Puerto Rico", flag: "🇵🇷" },
  { name: "Raúl", country: "Venezuela", flag: "🇻🇪" },
  { name: "Sergio", country: "Paraguay", flag: "🇵🇾" },
];

export const purchaseTimes = ["just now", "2 minutes ago", "4 minutes ago", "6 minutes ago", "9 minutes ago"];
